import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';

import {
  CancelSlotsParams,
  IndexStripeSubscriptions,
  useAccountsService,
  useEmployersService,
  useOrganizationsService,
} from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { showToast } from '@restworld/ui-ds';
import { FlexibleSlot } from '@restworld/utility-types';

export const flexibleSlotQueryKey = ['flexible-slots'];
export const subscriptionSlotQueryKey = ['subscription-slots'];
export const isSubscriptionFeedbacksSubmittedQueryKey = ['is_subscription_feedbacks_submitted'];

// --- fetch flexible slots query --- //
export const useFlexibleSlotsQuery = () => {
  const accountsService = useAccountsService();
  const { user } = useAuth();

  return useQuery({
    queryKey: flexibleSlotQueryKey,
    queryFn: async () =>
      (
        await accountsService.listFlexibleSlots({
          limit: 500,
          offset: 0,
          organizationId: user?.organization_id,
          status: ['active', 'idle'],
          dateTruncExpiresAt: true,
        })
      ).data.data,
  });
};
export const useFlexibleSlots = () => useQueryClient().getQueryData<FlexibleSlot[]>(flexibleSlotQueryKey);

export const useFlexibleSlotsLoading = () =>
  useQueryClient().getQueryState<FlexibleSlot[]>(flexibleSlotQueryKey).status === 'pending';

// --- fetch subscription slots query --- //
export const useFlexibleSubscriptionSlotsQuery = () => {
  const organizationsService = useOrganizationsService();
  const { user } = useAuth();

  return useQuery({
    queryKey: subscriptionSlotQueryKey,
    queryFn: async () =>
      (
        await organizationsService.fetchStripeSubscriptions({
          organizationId: user?.organization_id,
        })
      ).data,
  });
};
export const useFlexibleSubscriptionSlots = () =>
  useQueryClient().getQueryData<IndexStripeSubscriptions>(subscriptionSlotQueryKey);

export const useSubscriptionSlotsLoading = () =>
  useQueryClient().getQueryState<FlexibleSlot[]>(subscriptionSlotQueryKey).status === 'pending';

// --- activate flexible slot mutation --- //
export const useFlexibleSlotActivateMutation = () => {
  const accountsService = useAccountsService();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation('payment-and-invoices');

  return useMutation({
    mutationFn: async () => await accountsService.activateFlexibleSlot({ organizationId: user?.organization_id }),
    mutationKey: ['activate-flexible-slot'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flexibleSlotQueryKey });
      showToast(t('flexible_slot_activation_successful'), 'success', {
        delay: 300,
        autoClose: 1000,
        position: 'top-right',
      });
    },
    onError(error) {
      showToast(t('failed_to_activate_flexible_slot'), 'error', {
        delay: 300,
        autoClose: 1000,
        position: 'top-right',
      });
      console.error(error, `Failed to activate flexible slot for organization ${user?.organization_id}`);
      return error;
    },
  });
};
// --- fetch is subscription feedbacks submitted  ---
export const useFetchIsSubscriptionFeedbacksSubmittedQuery = () => {
  const employerService = useEmployersService();
  return useQuery({
    queryKey: isSubscriptionFeedbacksSubmittedQueryKey,
    queryFn: async () => (await employerService.is_subscription_feedbacks_submitted()).data,
  });
};
export const useIsSubscriptionFeedbacksSubmitted = () =>
  useQueryClient().getQueryData<boolean>(isSubscriptionFeedbacksSubmittedQueryKey);
// --- cancel flexible and subscription mutation --- //
export const useCancelFlexibleSubscriptionMutation = () => {
  const accountsService = useAccountsService();
  const queryClient = useQueryClient();
  const { t } = useTranslation('payment-and-invoices');
  return useMutation({
    mutationFn: async (params: CancelSlotsParams) => (await accountsService.cancelSlots(params)).data,
    onSuccess: (_, variables) => {
      showToast(
        t(variables.flexibleSlotIds ? 'flexible_slot_cancellation_successful' : 'subscription_cancelled_successfully'),
        'success',
        {
          delay: 300,
          autoClose: 4000,
          position: 'top-right',
        }
      );
      if (variables.flexibleSlotIds) queryClient.invalidateQueries({ queryKey: flexibleSlotQueryKey });
      else queryClient.invalidateQueries({ queryKey: subscriptionSlotQueryKey });
    },
    onError(error, variables) {
      showToast(
        t(variables.flexibleSlotIds ? 'failed_to_cancel_flexible_slot' : 'failed_to_cancel_subscription'),
        'error',
        {
          delay: 300,
          autoClose: 4000,
          position: 'top-right',
        }
      );
      console.error(
        error,
        variables.flexibleSlotIds
          ? `Failed to cancel flexible slot for organization ${variables?.employerSubscriptionFeedback?.organization_id}`
          : `Failed to cancel stripe subscription for ${variables.stripeSubscriptionId}`
      );
      return error;
    },
  });
};
